import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./App.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { RecruitingCacheProvider } from "./context/RecruitingCacheContext";
import { PageMetadataProvider } from "./context/PageMetadataContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import Navbar from "./components/shared/Navigation/Navbar";
import SidebarNavigation from "./components/shared/Navigation/SidebarNavigation";
import MobileTopNavbar from "./components/shared/Navigation/MobileTopNavbar";
import MainContentWrapper from "./components/shared/MainContentWrapper/MainContentWrapper";
import PublicHomePage from "./components/Home/PublicHomePage";
import DashboardPage from "./components/Dashboard/DashboardPage";
import RepsPage from "./components/Reps/RepsPage";
import TrainingMainPage from "./components/Training/TrainingMainPage";
import PerformancePage from "./components/Performance/PerformancePage";
import CourseFormPage from "./components/Training/CourseFormPage";
import RepTrainingPage from "./components/Training/RepTrainingPage";
import ProgramFormPage from "./components/Training/ProgramFormPage";
import AdminMainPage from "./components/Organization/AdminMainPage";
// import ReportsPage from "./components/Organization/ReportsPage";
import GroupDetailsPage from "./components/Organization/GroupDetailsPage";
import ProgramDetailsPage from "./components/Training/ProgramDetailsPage";
import LoginPage from "./components/Authentication/LoginPage";
import SignUpPage from "./components/Authentication/SignUpPage";
import ForgotPasswordPage from "./components/Authentication/ForgotPasswordPage";
import ResetPasswordPage from "./components/Authentication/ResetPasswordPage";
import ResetPinPage from "./components/Authentication/ResetPinPage";
import AccountPage from "./components/Account/AccountPage";
import EmailVerificationPage from "./components/Authentication/EmailVerificationPage";
import SupportPage from "./components/Support/SupportPage";
import PrivacyPolicyPage from "./components/PrivacyPolicy/PrivacyPolicyPage";
import TermsOfServicePage from "./components/TermsOfService/TermsOfServicePage";
import RoleProtectedRoute from "./components/shared/Navigation/RoleProtectedRoute";
import CompanyPage from "./components/Company/CompanyPage";
import CompanyUsersPage from "./components/Company/CompanyUsersPage";
import UserPayDetailsPage from "./components/Company/UserPayDetailsPage";
import SubscriptionPage from "./components/Company/SubscriptionPage";
// import SellingYearsPage from "./components/Company/SellingYearsPage";
import TeamDetailsPage from "./components/Company/TeamDetailsPage";
import SeasonDetailsPage from "./components/Company/SeasonDetailsPage";
import CompanyAssignmentPage from "./components/Authentication/CompanyAssignmentPage";
import ClientDetailsPage from "./components/Client/ClientDetailsPage";
import AddCompanyPage from "./components/Client/AddCompanyPage";
import DownlineManagementPage from "./components/RepManagement/DownlineManagementPage";
import RepDetailsPage from "./components/RepManagement/RepDetailsPage";
import AddRepPage from "./components/RepManagement/AddRepPage";
import AddDealPage from "./components/RepManagement/AddDealPage";
import AddRepDealPage from "./components/RepManagement/AddRepDealPage";
import BulkDealImportPage from "./components/RepManagement/BulkDealImportPage";
import RevenueImportPage from "./components/RepManagement/RevenueImportPage";
import EditDealPage from "./components/RepManagement/EditDealPage";
import DealDetailsPage from "./components/RepManagement/DealDetailsPage";
import ProgramRepsPage from "./components/RepManagement/ProgramRepsPage";
import RepProgramProgressPage from "./components/RepManagement/RepProgramProgressPage";
import RecruitingPage from "./components/Recruiting/RecruitingPage";
import RecruitingDashboard from "./components/Recruiting/RecruitingDashboard";
import ColdLeadsPage from "./components/Recruiting/ColdLeadsPage";
import ListViewPage from "./components/Recruiting/ListViewPage";
import RecruitingDocumentsPage from "./components/Recruiting/RecruitingDocumentsPage";
import DocumentList from "./components/ESignature/DocumentList";
import SigningPage from "./components/ESignature/SigningPage";
import TemplateList from "./components/ESignature/TemplateList";
import TemplateEditor from "./components/ESignature/TemplateEditor";
import PitchReviewPage from "./components/PitchReview/PitchReviewPage";
import ManagerPitchReviewsPage from "./components/PitchReview/ManagerPitchReviewsPage";
import { getDefaultRoute } from "./config/routes";
import { MantineProvider } from "@mantine/core";
import { theme } from "./styles/theme";
import "@mantine/core/styles.css";
import "@mantine/tiptap/styles.css";
import CustomerSupportPage from "./components/Client/CustomerSupportPage";
import AdminCompanyUsersPage from "./components/Client/AdminCompanyUsersPage";
import AdminSubscriptionPage from "./components/Client/AdminSubscriptionPage";
import ErrorHandlerInitializer from "./components/shared/ErrorHandlerInitializer";
import AdminPage from "./components/Admin/AdminPage";
import AgoraChatPage from "./components/Admin/AgoraChatPage";
import AdminTokenLoginPage from "./components/Authentication/AdminTokenLoginPage";
import IncentiveFormPage from "./components/Organization/IncentiveFormPage";
import AttendanceCheckInPage from "./components/Attendance/AttendanceCheckInPage";
import InfoVerificationPage from "./components/Verification/InfoVerificationPage";
import WelcomeSurveyPage from "./components/Verification/WelcomeSurveyPage";
import WelcomeSurveyAdminPage from "./components/WelcomeSurvey/WelcomeSurveyAdminPage";
import WelcomeSurveySettingsPage from "./components/WelcomeSurvey/WelcomeSurveySettingsPage";
import WelcomeSurveyRepQualityPage from "./components/WelcomeSurvey/WelcomeSurveyRepQualityPage";
import WelcomeSurveyAccountQualityPage from "./components/WelcomeSurvey/WelcomeSurveyAccountQualityPage";
// import FieldRoutesSetupPage from "./components/FieldRoutes/FieldRoutesSetupPage";

// Component to handle root route redirection
const RootRedirect = () => {
  const { isAuthenticated, authLoading, user } = useAuth();

  if (authLoading) {
    return null; // Don't render anything while loading
  }

  return <Navigate to={getDefaultRoute(isAuthenticated(), user)} replace />;
};

const CourseToTrainingRedirect = () => {
  const { id } = useParams();
  const courseId = Number(id);
  return (
    <Navigate
      to="/training"
      replace
      state={Number.isFinite(courseId) ? { courseId } : undefined}
    />
  );
};

// Component to redirect authenticated users away from public pages
const PublicRoute = ({ children }) => {
  const { isAuthenticated, authLoading, user } = useAuth();
  const [searchParams] = useSearchParams();

  if (authLoading) {
    return children; // Show the current page content while loading
  }

  if (isAuthenticated()) {
    const redirectTo = searchParams.get("redirect");
    return (
      <Navigate
        to={redirectTo || getDefaultRoute(isAuthenticated(), user)}
        replace
      />
    );
  }

  return children;
};

// Component to conditionally render the appropriate navbar
const NavbarSelector = ({ sidebarState, onSidebarToggle, onMobileClose }) => {
  const { isAuthenticated, authLoading } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return null; // Don't render navbar while loading
  }

  // Hide navbar on login page and public signing page only
  if (
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/signup/:token" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/verify-email" ||
    location.pathname === "/verify-email/:token" ||
    location.pathname === "/admin-token/:token" ||
    location.pathname === "/company-assignment" ||
    location.pathname.startsWith("/sign/") ||
    location.pathname.startsWith("/welcomesurvey")
  ) {
    return null;
  }

  return isAuthenticated() ? (
    <>
      {/* Mobile top navbar - only visible on mobile */}
      <MobileTopNavbar
        isMenuOpen={sidebarState.isMobileOpen}
        onMenuToggle={onSidebarToggle}
      />
      {/* Sidebar navigation - slide-in drawer on mobile, opened from the top navbar */}
      <SidebarNavigation
        isCollapsed={sidebarState.isCollapsed}
        isMobileOpen={sidebarState.isMobileOpen}
        onToggle={onSidebarToggle}
        onMobileClose={onMobileClose}
      />
    </>
  ) : (
    <Navbar />
  );
};

// Component to wrap content with appropriate layout
const ContentWrapper = React.memo(({ children }) => {
  const { isAuthenticated, authLoading } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return children; // Don't wrap while loading
  }

  // Don't wrap public signing page or verify-email pages
  const isSigningPage = location.pathname.startsWith("/sign/");
  const isVerifyEmailPage =
    location.pathname === "/verify-email" ||
    location.pathname.startsWith("/verify-email/");
  const isCompanyAssignment = location.pathname.startsWith(
    "/company-assignment"
  );
  const isWelcomeSurveyPage = location.pathname.startsWith("/welcomesurvey");

  if (
    isSigningPage ||
    isVerifyEmailPage ||
    isCompanyAssignment ||
    isWelcomeSurveyPage
  ) {
    return children;
  }
  // For authenticated users, wrap with MainContentWrapper
  // For public pages, render content directly
  return isAuthenticated() ? (
    <MainContentWrapper hasSidebar={true}>{children}</MainContentWrapper>
  ) : (
    children
  );
});

/* Mantine keeps its own colour scheme, so it has to be told which one we are
   on - otherwise its components stay light while the CSS tokens go dark.
   Needs to sit under ThemeProvider to read the scheme, hence its own
   component rather than an inline hook call in App. */
function ThemedMantineProvider({ children }) {
  const { colorScheme } = useTheme();

  return (
    <MantineProvider theme={theme} forceColorScheme={colorScheme}>
      {children}
    </MantineProvider>
  );
}

/* Both halves of theming as one wrapper, so it drops into the provider tree
   where MantineProvider used to sit. */
function AppThemeProvider({ children }) {
  return (
    <ThemeProvider>
      <ThemedMantineProvider>{children}</ThemedMantineProvider>
    </ThemeProvider>
  );
}

function App() {
  // Sidebar state management with persistence
  const [sidebarState, setSidebarState] = React.useState(() => {
    // Load saved sidebar state from localStorage
    let savedCollapsed = false;
    try {
      if (typeof Storage !== "undefined") {
        const saved = localStorage.getItem("sidebarCollapsed");
        if (saved !== null) {
          savedCollapsed = JSON.parse(saved);
        }
      }
    } catch (error) {
      // Failed to load sidebar state from localStorage
    }

    return {
      isCollapsed: savedCollapsed,
      isMobileOpen: false,
      isMobile: false,
    };
  });

  // Handle sidebar toggle
  const handleSidebarToggle = React.useCallback(() => {
    setSidebarState(prev => {
      const newState = { ...prev };

      if (prev.isMobile) {
        // On mobile, toggle the overlay open/close
        newState.isMobileOpen = !prev.isMobileOpen;
      } else {
        // On desktop, toggle collapsed/expanded
        newState.isCollapsed = !prev.isCollapsed;

        // Persist desktop sidebar state to localStorage
        try {
          if (typeof Storage !== "undefined") {
            localStorage.setItem(
              "sidebarCollapsed",
              JSON.stringify(!prev.isCollapsed)
            );
          }
        } catch (error) {
          // Failed to save sidebar state to localStorage
        }
      }

      return newState;
    });
  }, []);

  // Close mobile sidebar when clicking outside (handled by backdrop)
  const handleMobileClose = React.useCallback(() => {
    setSidebarState(prev => ({
      ...prev,
      isMobileOpen: false,
    }));
  }, []);

  // Handle window resize to detect mobile
  React.useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      setSidebarState(prev => ({
        ...prev,
        isMobile,
        // Auto-collapse on mobile, preserve desktop state
        isCollapsed: isMobile ? true : prev.isCollapsed,
        // Close mobile sidebar when switching to desktop
        isMobileOpen: isMobile ? prev.isMobileOpen : false,
      }));
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update body classes based on sidebar state to avoid prop drilling
  React.useEffect(() => {
    const body = document.body;

    // Remove all sidebar-related classes
    body.classList.remove(
      "sidebar-expanded",
      "sidebar-collapsed",
      "mobile-sidebar-open",
      "mobile-sidebar-closed",
      "is-mobile",
      "has-mobile-top-navbar"
    );

    if (sidebarState.isMobile) {
      body.classList.add("is-mobile");
      // Add class for mobile top navbar when authenticated and on mobile

      body.classList.add(
        sidebarState.isMobileOpen
          ? "mobile-sidebar-open"
          : "mobile-sidebar-closed"
      );
    } else {
      body.classList.add(
        sidebarState.isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
      );
    }

    // Cleanup function to remove classes when component unmounts
    return () => {
      body.classList.remove(
        "sidebar-expanded",
        "sidebar-collapsed",
        "mobile-sidebar-open",
        "mobile-sidebar-closed",
        "is-mobile",
        "has-mobile-top-navbar"
      );
    };
  }, [
    sidebarState.isCollapsed,
    sidebarState.isMobileOpen,
    sidebarState.isMobile,
  ]);

  // Add keyboard shortcuts for sidebar
  React.useEffect(() => {
    const handleKeyDown = e => {
      // Ctrl/Cmd + B to toggle sidebar
      if ((e.ctrlKey || e.metaKey) && e.key === "b") {
        e.preventDefault();
        handleSidebarToggle();
      }

      // Escape key to close mobile sidebar
      if (
        e.key === "Escape" &&
        sidebarState.isMobile &&
        sidebarState.isMobileOpen
      ) {
        e.preventDefault();
        handleMobileClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    handleSidebarToggle,
    handleMobileClose,
    sidebarState.isMobile,
    sidebarState.isMobileOpen,
  ]);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <AppThemeProvider>
      <GoogleOAuthProvider clientId={googleClientId}>
        <GoogleMapsProvider>
          <AuthProvider>
            <ToastProvider>
              <RecruitingCacheProvider>
                <PageMetadataProvider>
                  <Router>
                    <div className="App">
                      <ErrorHandlerInitializer />
                      <NavbarSelector
                        sidebarState={sidebarState}
                        onSidebarToggle={handleSidebarToggle}
                        onMobileClose={handleMobileClose}
                      />
                      <ContentWrapper>
                        <Routes>
                          <Route path="/" element={<RootRedirect />} />
                          <Route
                            path="/home"
                            element={
                              <PublicRoute>
                                <PublicHomePage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/login"
                            element={
                              <PublicRoute>
                                <LoginPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/signup"
                            element={
                              <PublicRoute>
                                <SignUpPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/signup/:token"
                            element={
                              <PublicRoute>
                                <SignUpPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/forgot-password"
                            element={
                              <PublicRoute>
                                <ForgotPasswordPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/reset-password"
                            element={
                              <PublicRoute>
                                <ResetPasswordPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/reset-pin"
                            element={
                              <PublicRoute>
                                <ResetPinPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/admin-token/:token"
                            element={<AdminTokenLoginPage />}
                          />
                          <Route
                            path="/verify-email"
                            element={<EmailVerificationPage />}
                          />
                          <Route
                            path="/verify-email/:token"
                            element={<EmailVerificationPage />}
                          />
                          <Route
                            path="/company-assignment"
                            element={<CompanyAssignmentPage />}
                          />
                          {/* The client the survey belongs to leads the path so
                              the API host can be built without a login. */}
                          <Route
                            path="/welcomesurvey/:client/:surveyCode"
                            element={<InfoVerificationPage />}
                          />
                          <Route
                            path="/surveycustomer/:clientcode"
                            element={<WelcomeSurveyPage />}
                          />
                          <Route path="/support" element={<SupportPage />} />
                          <Route
                            path="/privacy"
                            element={<PrivacyPolicyPage />}
                          />
                          <Route
                            path="/terms"
                            element={<TermsOfServicePage />}
                          />
                          {/* <Route
                            path="/features/training"
                            element={
                              <PublicRoute>
                                <TrainingLandingPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/features/recruiting"
                            element={
                              <PublicRoute>
                                <RecruitingLandingPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/features/performance"
                            element={
                              <PublicRoute>
                                <PerformanceLandingPage />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/features/communication"
                            element={
                              <PublicRoute>
                                <CommunicationLandingPage />
                              </PublicRoute>
                            }
                          /> */}
                          <Route
                            path="/dashboard"
                            element={
                              <RoleProtectedRoute>
                                <DashboardPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reps"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <RepsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/training"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REP", "MANAGER"]}
                              >
                                <TrainingMainPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/training-main"
                            element={
                              <RoleProtectedRoute>
                                <TrainingMainPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/performance"
                            element={
                              <RoleProtectedRoute>
                                <PerformancePage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/course/:id"
                            element={<CourseToTrainingRedirect />}
                          />
                          <Route
                            path="/training-courses/new"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <CourseFormPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/training-courses/edit/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <CourseFormPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/rep-training"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <RepTrainingPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/programs"
                            element={
                              <Navigate to="/admin?tab=programs" replace />
                            }
                          />
                          <Route
                            path="/programs/new"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <ProgramFormPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/programs/edit/:id"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <ProgramFormPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <AdminMainPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin-main"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <AdminMainPage />
                              </RoleProtectedRoute>
                            }
                          />
                          {/* <Route
                            path="/reports"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <ReportsPage />
                              </RoleProtectedRoute>
                            }
                          /> */}
                          <Route
                            path="/welcome-survey"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <WelcomeSurveyAdminPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/welcome-survey/rep-quality"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <WelcomeSurveyRepQualityPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/welcome-survey/account-quality"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <WelcomeSurveyAccountQualityPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/welcome-survey/settings"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <WelcomeSurveySettingsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          {/* <Route
                            path="/fieldroutes-setup"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <FieldRoutesSetupPage />
                              </RoleProtectedRoute>
                            }
                          /> */}
                          <Route
                            path="/group/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <GroupDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/programs/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "REP"]}
                              >
                                <ProgramDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/video"
                            element={<Navigate to="/training" replace />}
                          />
                          <Route
                            path="/downline"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <DownlineManagementPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/downline/programs/:programId"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <ProgramRepsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/downline/programs/:programId/rep/:userSeasonId"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <RepProgramProgressPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reps/downline"
                            element={<Navigate to="/downline" replace />}
                          />
                          <Route
                            path="/reps/health"
                            element={<Navigate to="/downline" replace />}
                          />
                          <Route
                            path="/reps/add"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <AddRepPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/deals/add"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <AddDealPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/deals/:id/edit"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <EditDealPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/deals/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <DealDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reps/import"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <BulkDealImportPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reps/import-revenue"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <RevenueImportPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reps/:id/deal/add"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <AddRepDealPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reps/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <RepDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/user-pay-details/:userId"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <UserPayDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/recruiting"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <RecruitingDashboard />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/recruiting/pipeline"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <RecruitingPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/recruiting/cold"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <ColdLeadsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/recruiting/list"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <ListViewPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/recruiting/documents"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <RecruitingDocumentsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/esignature"
                            element={
                              <RoleProtectedRoute>
                                <DocumentList />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/esignature/documents"
                            element={
                              <RoleProtectedRoute>
                                <DocumentList />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/esignature/templates"
                            element={
                              <RoleProtectedRoute>
                                <TemplateList />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/esignature/templates/:templateId/edit"
                            element={
                              <RoleProtectedRoute>
                                <TemplateEditor />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/esignature/documents/:documentId/edit"
                            element={
                              <RoleProtectedRoute>
                                <TemplateEditor />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/sign/:token"
                            element={<SigningPage />}
                          />
                          <Route
                            path="/account"
                            element={
                              <RoleProtectedRoute>
                                <AccountPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/pitch-review/:id"
                            element={
                              <RoleProtectedRoute>
                                <PitchReviewPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/pitch-reviews"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["MANAGER", "COMPANY_ADMIN"]}
                              >
                                <ManagerPitchReviewsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/company"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <CompanyPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/company-downline"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <CompanyUsersPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/subscription"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <SubscriptionPage />
                              </RoleProtectedRoute>
                            }
                          />
                          {/* <Route
                            path="/selling-years"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <SellingYearsPage />
                              </RoleProtectedRoute>
                            }
                          /> */}
                          <Route
                            path="/teams/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <TeamDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/season/:id"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["COMPANY_ADMIN"]}
                              >
                                <SeasonDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/clients"
                            element={<Navigate to="/reppi-admin" replace />}
                          />
                          <Route
                            path="/client/new"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <AddCompanyPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/client/:code"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <ClientDetailsPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/client/:code/subscription"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <AdminSubscriptionPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/customer-support/:code"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <CustomerSupportPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/company-users/:code"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <AdminCompanyUsersPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/incentives/new"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <IncentiveFormPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/incentives/edit/:id"
                            element={
                              <RoleProtectedRoute requiredRole={["MANAGER"]}>
                                <IncentiveFormPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/reppi-admin"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <AdminPage />
                              </RoleProtectedRoute>
                            }
                          />
                          <Route
                            path="/agora-chat"
                            element={
                              <RoleProtectedRoute
                                requiredRole={["REPPI_ADMIN"]}
                              >
                                <AgoraChatPage />
                              </RoleProtectedRoute>
                            }
                          />
                          {/* QR check-in page — accessible to all authenticated users */}
                          <Route
                            path="/attendance/check-in"
                            element={<AttendanceCheckInPage />}
                          />
                          {/* Catch-all route for non-existent paths - redirect to home */}
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                        </Routes>
                      </ContentWrapper>
                      {/* Mobile backdrop */}
                      {sidebarState.isMobile && sidebarState.isMobileOpen && (
                        <div
                          className="mobile-sidebar-backdrop"
                          onClick={handleMobileClose}
                          onTouchEnd={handleMobileClose}
                          role="button"
                          tabIndex={-1}
                          aria-label="Close sidebar"
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleMobileClose();
                            }
                          }}
                        />
                      )}
                      {/* Mobile swipe area for opening sidebar */}
                      {sidebarState.isMobile && !sidebarState.isMobileOpen && (
                        <div
                          className="mobile-swipe-area"
                          onTouchStart={e => {
                            const touch = e.touches[0];
                            if (touch.clientX < 20) {
                              // Store initial touch for swipe detection
                              e.currentTarget.dataset.touchStart =
                                touch.clientX;
                              e.currentTarget.dataset.touchStartY =
                                touch.clientY;
                            }
                          }}
                          onTouchMove={e => {
                            const touchStart = parseFloat(
                              e.currentTarget.dataset.touchStart || "0"
                            );
                            const touchStartY = parseFloat(
                              e.currentTarget.dataset.touchStartY || "0"
                            );

                            if (touchStart > 0) {
                              const currentX = e.touches[0].clientX;
                              const currentY = e.touches[0].clientY;
                              const deltaX = Math.abs(currentX - touchStart);
                              const deltaY = Math.abs(currentY - touchStartY);

                              // Prevent vertical scrolling if horizontal swipe is detected
                              if (deltaX > deltaY && deltaX > 10) {
                                e.preventDefault();
                              }
                            }
                          }}
                          onTouchEnd={e => {
                            const touchStart = parseFloat(
                              e.currentTarget.dataset.touchStart || "0"
                            );
                            const touchEnd = e.changedTouches[0].clientX;
                            const distance = touchEnd - touchStart;

                            // Open sidebar on right swipe from left edge
                            if (distance > 50 && touchStart < 20) {
                              handleSidebarToggle();
                            }

                            // Clean up data attributes
                            delete e.currentTarget.dataset.touchStart;
                            delete e.currentTarget.dataset.touchStartY;
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </Router>
                </PageMetadataProvider>
              </RecruitingCacheProvider>
            </ToastProvider>
          </AuthProvider>
        </GoogleMapsProvider>
      </GoogleOAuthProvider>
    </AppThemeProvider>
  );
}

export default App;
